// ブレンド合成のフラグメントシェーダ。
// render/blendModes.ts の TS 実装と同じ式 (テストのオラクル)。ストレートアルファ。

import { BLEND_MODES, type BlendMode } from '@/types/document'

/** GLSL の uMode に渡す番号 (BLEND_MODES の順序) */
export function blendModeIndex(mode: BlendMode): number {
  return BLEND_MODES.indexOf(mode)
}

const MODE_CONSTS = BLEND_MODES.map((m, i) => `const int M_${m.toUpperCase().replace(/-/g, '_')} = ${i};`).join('\n')

export const BLEND_FRAG = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
in vec2 vPos;                 // 文書座標
uniform sampler2D uDst;       // 合成途中 (文書サイズ)
uniform sampler2D uSrc;       // レイヤーテクスチャ
uniform sampler2D uMask;      // レイヤーマスク (R8)。uHasMask == 0 なら未使用
uniform vec2 uDocSize;
uniform vec4 uSrcRect;        // src の文書座標矩形 x,y,w,h (uUseMatrix == 0 のとき)
uniform mat3 uSrcInv;         // 文書座標 → src テクセル座標 (uUseMatrix == 1 のとき)
uniform vec2 uSrcSize;
uniform int uUseMatrix;
uniform vec4 uMaskRect;
uniform int uHasMask;
uniform sampler2D uClip;      // クリッピングマスクのベース (α だけ使う)
uniform vec4 uClipRect;
uniform int uHasClip;
uniform float uOpacity;
uniform int uMode;
uniform float uSeed;
uniform int uBlendIf;          // ブレンド条件を使うか
uniform int uBlendIfCh;        // 0 gray, 1 r, 2 g, 3 b
uniform vec4 uBlendIfThis;     // このレイヤー [lo0, lo1, hi0, hi1] (0..1)
uniform vec4 uBlendIfUnder;    // 下になっているレイヤー
out vec4 outColor;

${MODE_CONSTS}

float lum(vec3 c) { return dot(c, vec3(0.3, 0.59, 0.11)); }
vec3 clipColor(vec3 c) {
  float l = lum(c);
  float n = min(c.r, min(c.g, c.b));
  float x = max(c.r, max(c.g, c.b));
  if (n < 0.0) c = l + (c - l) * l / (l - n);
  if (x > 1.0) c = l + (c - l) * (1.0 - l) / (x - l);
  return c;
}
vec3 setLum(vec3 c, float l) { return clipColor(c + (l - lum(c))); }
float sat(vec3 c) { return max(c.r, max(c.g, c.b)) - min(c.r, min(c.g, c.b)); }
vec3 setSat(vec3 c, float s) {
  float mx = max(c.r, max(c.g, c.b));
  float mn = min(c.r, min(c.g, c.b));
  vec3 o = vec3(0.0);
  if (mx > mn) {
    o = (c - mn) * s / (mx - mn);
  }
  return o;
}

float multiplyF(float b, float s) { return b * s; }
float screenF(float b, float s) { return b + s - b * s; }
float hardLightF(float b, float s) { return s <= 0.5 ? multiplyF(b, 2.0 * s) : screenF(b, 2.0 * s - 1.0); }
float colorDodgeF(float b, float s) { return b == 0.0 ? 0.0 : (s >= 1.0 ? 1.0 : min(1.0, b / (1.0 - s))); }
float colorBurnF(float b, float s) { return b >= 1.0 ? 1.0 : (s <= 0.0 ? 0.0 : 1.0 - min(1.0, (1.0 - b) / s)); }
float softLightF(float b, float s) {
  if (s <= 0.5) return b - (1.0 - 2.0 * s) * b * (1.0 - b);
  float d = b <= 0.25 ? ((16.0 * b - 12.0) * b + 4.0) * b : sqrt(b);
  return b + (2.0 * s - 1.0) * (d - b);
}
float vividLightF(float b, float s) { return s <= 0.5 ? colorBurnF(b, 2.0 * s) : colorDodgeF(b, 2.0 * s - 1.0); }

float blendCh(int mode, float b, float s) {
  if (mode == M_NORMAL || mode == M_DISSOLVE) return s;
  if (mode == M_DARKEN) return min(b, s);
  if (mode == M_MULTIPLY) return multiplyF(b, s);
  if (mode == M_COLOR_BURN) return colorBurnF(b, s);
  if (mode == M_LINEAR_BURN) return clamp(b + s - 1.0, 0.0, 1.0);
  if (mode == M_LIGHTEN) return max(b, s);
  if (mode == M_SCREEN) return screenF(b, s);
  if (mode == M_COLOR_DODGE) return colorDodgeF(b, s);
  if (mode == M_LINEAR_DODGE) return clamp(b + s, 0.0, 1.0);
  if (mode == M_OVERLAY) return hardLightF(s, b);
  if (mode == M_SOFT_LIGHT) return softLightF(b, s);
  if (mode == M_HARD_LIGHT) return hardLightF(b, s);
  if (mode == M_VIVID_LIGHT) return vividLightF(b, s);
  if (mode == M_LINEAR_LIGHT) return clamp(b + 2.0 * s - 1.0, 0.0, 1.0);
  if (mode == M_PIN_LIGHT) return s <= 0.5 ? min(b, 2.0 * s) : max(b, 2.0 * s - 1.0);
  if (mode == M_HARD_MIX) return (b + s) >= 1.0 ? 1.0 : 0.0;
  if (mode == M_DIFFERENCE) return abs(b - s);
  if (mode == M_EXCLUSION) return b + s - 2.0 * b * s;
  if (mode == M_SUBTRACT) return clamp(b - s, 0.0, 1.0);
  if (mode == M_DIVIDE) return s <= 0.0 ? 1.0 : clamp(b / s, 0.0, 1.0);
  return s;
}

vec3 blendRgb(int mode, vec3 b, vec3 s) {
  if (mode == M_HUE) return setLum(setSat(s, sat(b)), lum(b));
  if (mode == M_SATURATION) return setLum(setSat(b, sat(s)), lum(b));
  if (mode == M_COLOR) return setLum(s, lum(b));
  if (mode == M_LUMINOSITY) return setLum(b, lum(s));
  if (mode == M_DARKER_COLOR) return (b.r + b.g + b.b) <= (s.r + s.g + s.b) + 1e-4 ? b : s;
  if (mode == M_LIGHTER_COLOR) return (b.r + b.g + b.b) + 1e-4 >= (s.r + s.g + s.b) ? b : s;
  return vec3(blendCh(mode, b.r, s.r), blendCh(mode, b.g, s.g), blendCh(mode, b.b, s.b));
}

float blendIfRange(float v, vec4 r) {
  float lo = r.y > r.x ? clamp((v - r.x) / (r.y - r.x), 0.0, 1.0) : (v >= r.x ? 1.0 : 0.0);
  float hi = r.w > r.z ? clamp((r.w - v) / (r.w - r.z), 0.0, 1.0) : (v <= r.w ? 1.0 : 0.0);
  return lo * hi;
}
float blendIfValue(vec3 c) {
  if (uBlendIfCh == 1) return c.r;
  if (uBlendIfCh == 2) return c.g;
  if (uBlendIfCh == 3) return c.b;
  return dot(c, vec3(0.299, 0.587, 0.114));
}

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

void main() {
  vec2 uvDst = vPos / uDocSize;
  vec4 dst = texture(uDst, uvDst);
  vec2 uvSrc;
  if (uUseMatrix == 1) {
    vec3 sp = uSrcInv * vec3(vPos, 1.0);
    uvSrc = (sp.xy / sp.z) / uSrcSize;
    if (sp.z <= 0.0) uvSrc = vec2(-1.0);
  } else {
    uvSrc = (vPos - uSrcRect.xy) / uSrcRect.zw;
  }
  vec4 src = vec4(0.0);
  if (uvSrc.x >= 0.0 && uvSrc.x <= 1.0 && uvSrc.y >= 0.0 && uvSrc.y <= 1.0) src = texture(uSrc, uvSrc);
  float mask = 1.0;
  if (uHasMask == 1) {
    vec2 uvM = (vPos - uMaskRect.xy) / uMaskRect.zw;
    mask = (uvM.x >= 0.0 && uvM.x <= 1.0 && uvM.y >= 0.0 && uvM.y <= 1.0) ? texture(uMask, uvM).r : 0.0;
  }
  float clipA = 1.0;
  if (uHasClip == 1) {
    vec2 uvC = (vPos - uClipRect.xy) / uClipRect.zw;
    clipA = (uvC.x >= 0.0 && uvC.x <= 1.0 && uvC.y >= 0.0 && uvC.y <= 1.0) ? texture(uClip, uvC).a : 0.0;
  }
  float as = clamp(src.a * uOpacity * mask * clipA, 0.0, 1.0);
  if (uBlendIf == 1) as *= blendIfRange(blendIfValue(src.rgb), uBlendIfThis) * blendIfRange(blendIfValue(dst.rgb), uBlendIfUnder);
  if (uMode == M_DISSOLVE) {
    as = hash12(floor(vPos) + uSeed) < as ? 1.0 : 0.0;
  }
  float ab = dst.a;
  float ao = as + ab * (1.0 - as);
  if (ao <= 0.0) { outColor = vec4(0.0); return; }
  vec3 B = blendRgb(uMode, dst.rgb, src.rgb);
  vec3 co = (src.rgb * as * (1.0 - ab) + dst.rgb * ab * (1.0 - as) + as * ab * B) / ao;
  outColor = vec4(clamp(co, 0.0, 1.0), ao);
}
`
